/// <reference types="@angular/localize" />

// frontend/src/main.ts
import '@angular/common/locales/global/de';
import { bootstrapApplication } from '@angular/platform-browser';
import { appConfig } from 'app/app.config';
import { App } from 'app/app';
import { provideApi } from 'api/provide-api';
import { authConfig } from 'app/auth/auth.config';
import {
  AbstractSecurityStorage,
  DefaultLocalStorageService,
  provideAuth,
} from 'angular-auth-oidc-client';
import { LOCALE_ID } from '@angular/core';

async function loadRuntimeConfig(): Promise<{ apiUrl: string }> {
  const resp = await fetch('./config.json', { cache: 'no-cache' });
  if (!resp.ok) {
    throw new Error(`/config.json could not be loaded (status: ${resp.status})`);
  }
  const cfg = await resp.json();
  if (!cfg || typeof cfg.apiUrl !== 'string' || !cfg.apiUrl.trim()) {
    throw new Error('/config.json must contain a non-empty "apiUrl" string property');
  }
  return { apiUrl: cfg.apiUrl.trim() };
}

function getPreferredLocale(): string {
  const supportedLocales = ['de', 'en'];

  const browserLocales =
    navigator.languages && navigator.languages.length > 0
      ? navigator.languages
      : [navigator.language];

  for (const browserLocale of browserLocales) {
    if (supportedLocales.includes(browserLocale)) {
      return browserLocale;
    }

    const baseLocale = browserLocale.split('-')[0];
    if (supportedLocales.includes(baseLocale)) {
      return baseLocale;
    }
  }

  return 'en-US';
}

(async () => {
  try {
    const cfg = await loadRuntimeConfig();
    const apiUrl = cfg.apiUrl;
    const locale = getPreferredLocale();

    const providers = [
      ...(appConfig.providers ?? []),
      { provide: LOCALE_ID, useValue: locale },
      // Provide the generated API client with the runtime apiUrl
      provideApi(apiUrl),
      provideAuth(authConfig),
      // Persist auth data across browser tabs / sessions
      { provide: AbstractSecurityStorage, useClass: DefaultLocalStorageService },
    ];

    await bootstrapApplication(App, { providers });
  } catch (err) {
    // Abort bootstrap and show an error page; console logging for diagnostics
    console.error('Application startup aborted due to configuration error:', err);

    // Minimal visible error for users / deployers
    document.body.style.margin = '0';
    document.body.style.fontFamily =
      'var(--app-font-family, "Open Sans", system-ui, -apple-system, Segoe UI, Roboto, "Helvetica Neue", Arial)';
    document.body.innerHTML = `
      <main style="padding:1.5rem;color:var(--ul-app-text-primary, var(--mat-sys-on-surface));">
        <h1 style="color:var(--ul-app-danger, var(--mat-sys-error))">Application startup error</h1>
        <p>Failed to load runtime configuration from <code>/config.json</code>.</p>
        <pre style="background:var(--ul-app-background-surface, var(--mat-sys-surface-container));padding:0.75rem;border-radius:0.375rem;white-space:pre-wrap;color:var(--ul-app-text-primary, var(--mat-sys-on-surface));">
${String(err)}
        </pre>
        <p>Please ensure a valid <code>/config.json</code> is present and contains an <code>apiUrl</code> property.</p>
      </main>
    `;
  }
})();
